import { useMutation, useQueryClient, type QueryKey, type UseMutationOptions, type UseMutationResult } from "@tanstack/react-query"
import { usePlanLimitToast } from "@/hooks/use-plan-limit-toast"
import { useToast } from "@/hooks/use-toast"
import { translateError } from "@/lib/error-messages"
import { reportApiRefusal } from "@/lib/errorReporter"

/**
 * Type-augments TanStack Query's mutation `meta` so the global `MutationCache`
 * policy in App.tsx can read `mutation.meta.entity` without casting. `entity`
 * carries the root query-key prefixes ("programs", "sessions", ...) that a
 * mutation affects — the global policy invalidates every query whose key
 * starts with one of these prefixes as soon as the mutation succeeds.
 */
declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      entity?: string[]
    }
  }
}

export interface UseApiMutationOptions<TData, TVariables, TContext = unknown>
  extends Omit<UseMutationOptions<TData, unknown, TVariables, TContext>, "mutationFn" | "meta"> {
  /**
   * Extra query keys to invalidate on success, beyond the automatic
   * prefix-invalidation the global `MutationCache` policy already performs
   * for every key in `entity`. Reserved for derived/cross-domain keys that
   * do not share a root with `entity` (coherence widgets, migration-status
   * banners, pricing-migration-status, ...). Runs before the global policy
   * (TanStack Query v5 fires the mutation's own callbacks before
   * `MutationCache`'s), but `invalidateQueries` is idempotent so the exact
   * ordering has no observable effect — the split is purely for readability.
   */
  invalidates?: (data: TData, variables: TVariables) => QueryKey[]
  /**
   * Leading line of the error toast shown automatically on failure. Defaults
   * to a generic message; the detail line always comes from
   * `translateError(error)`.
   */
  errorTitle?: string
  /**
   * `false` when the page reports the error itself, inline next to the action
   * (e.g. a dialog with a fix link): one error report, never toast + alert.
   */
  errorToast?: boolean
}

/**
 * Sole mutation pathway for the app. Every `useMutation` call site that talks
 * to the API MUST go through this factory instead of calling `useMutation`
 * directly, so that:
 *
 * - CSRF + auth headers are inherited for free (the `mutationFn` calls a
 *   `src/services/*.ts` function, which itself calls the shared `api` axios
 *   instance already configured with `X-Requested-With` + cookies).
 * - Errors surface a translated toast automatically — callers only need to
 *   supply `errorTitle` for the leading line, `translateError` fills the
 *   detail.
 * - `meta.entity` is always set (even to an explicit empty array for
 *   mutations with no cross-page effect), which is what lets the global
 *   `MutationCache` policy in App.tsx invalidate the right query prefixes
 *   without every page having to remember to do it by hand.
 *
 * `entity` accepts a single root key or several (e.g. a registration
 * mutation that also affects the parent session's occupancy count).
 */
export function useApiMutation<TData, TVariables = void, TContext = unknown>(
  entity: string | string[],
  mutationFn: (variables: TVariables) => Promise<TData>,
  options: UseApiMutationOptions<TData, TVariables, TContext> = {}
): UseMutationResult<TData, unknown, TVariables, TContext> {
  const { toast } = useToast()
  const showPlanLimit = usePlanLimitToast()
  const queryClient = useQueryClient()
  const entities = Array.isArray(entity) ? entity : [entity]
  const { invalidates, onSuccess, onError, errorTitle, errorToast = true, ...rest } = options

  return useMutation<TData, unknown, TVariables, TContext>({
    ...rest,
    mutationFn,
    meta: { entity: entities },
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidates?.(data, variables).forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey })
      })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    onError: (error, variables, onMutateResult, context) => {
      void reportApiRefusal(error)
      // Limite de plan (402) : l'écran de limite, jamais l'erreur générique.
      if (errorToast && !showPlanLimit(error)) {
        toast({
          title: errorTitle ?? "Une erreur est survenue",
          description: translateError(error),
          variant: "destructive",
        })
      }
      onError?.(error, variables, onMutateResult, context)
    },
  })
}
